import { useCallback, useEffect, useState } from 'react';
import posthog from '../posthog';
import { supabase } from '../supabase';

/** `connection_requests.platform`: ways to connect we don't support yet. */
export type ConnectionPlatform =
  | 'own_api'
  | 'aws_bedrock'
  | 'vertex_ai'
  | 'openai_assistants'
  | 'copilot_studio'
  | 'other';

/**
 * The current user's requests for connection types PromptSafe doesn't support
 * yet. Each request is saved, so we can see what people want and follow up.
 */
export function useConnectionRequests(workspaceId: string | undefined) {
  const [requested, setRequested] = useState<Set<ConnectionPlatform>>(
    new Set(),
  );

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    supabase
      .from('connection_requests')
      .select('platform')
      .eq('workspace_id', workspaceId)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Loading connection requests failed:', error);
          return;
        }
        setRequested(new Set((data ?? []).map((row) => row.platform)));
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const request = useCallback(
    async (platform: ConnectionPlatform, note?: string) => {
      if (!workspaceId) throw new Error('Workspace ID missing');
      const { error } = await supabase.from('connection_requests').insert({
        workspace_id: workspaceId,
        platform,
        note: note?.trim() || null,
      });
      if (error) throw new Error('Could not send the request. Try again.');

      posthog.capture('connection_requested', {
        platform,
        workspace_id: workspaceId,
      });
      setRequested((current) => new Set(current).add(platform));
    },
    [workspaceId],
  );

  return { requested, request };
}
